import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { ESSAYS, TAGS } from "../data/mock";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useSEO } from "../hooks/useSEO";

export const TagPage = () => {
  const { slug } = useParams();
  const tag = TAGS.find((t) => t.slug === slug);
  const matches = ESSAYS.filter((e) => (e.tags || []).includes(slug));

  useSEO({
    title: tag ? `Essays tagged "${tag.label}", Hunar Syal` : "Tag not found",
    description: tag
      ? `Every essay by Hunar Syal tagged ${tag.label}, ${matches.length} piece${matches.length === 1 ? "" : "s"} on this theme.`
      : undefined,
    url: typeof window !== "undefined" ? window.location.href : undefined,
  });

  if (!tag) return <Navigate to="/#essays" replace />;

  const otherTags = TAGS.filter((t) => t.slug !== slug);

  return (
    <div data-testid="tag-page" style={{ background: "var(--brand-cream)" }}>
      <Navbar />

      <section className="pt-28 md:pt-36 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/#essays"
            data-testid="tag-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold mb-8 hover:opacity-70"
            style={{ color: "var(--brand-muted)" }}
          >
            <ArrowLeft size={15} strokeWidth={2} />
            Back to all essays
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.3em] mb-4"
              style={{ color: "var(--brand-accent)" }}
            >
              Tag · {matches.length} essay{matches.length === 1 ? "" : "s"}
            </p>
            <h1
              className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.04]"
              style={{ color: "var(--brand-ink)" }}
              data-testid="tag-heading"
            >
              {tag.label}
            </h1>
          </motion.div>

          <div
            className="mt-14 divide-y"
            style={{ borderColor: "var(--brand-line)" }}
          >
            {matches.map((essay) => (
              <Link
                key={essay.id}
                to={`/essays/${essay.slug}`}
                data-testid={`tag-essay-${essay.id}`}
                className="group grid md:grid-cols-[auto_1fr_auto] items-start md:items-center gap-4 md:gap-10 py-8 md:py-10"
                style={{ borderTop: "1px solid var(--brand-line)" }}
              >
                <div
                  className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] md:min-w-[180px]"
                  style={{ color: "var(--brand-accent)" }}
                >
                  <span>{essay.category}</span>
                  <span className="w-1 h-1 rounded-full" style={{ background: "var(--brand-muted)" }} />
                  <span style={{ color: "var(--brand-muted)" }}>{essay.readTime}</span>
                </div>
                <div>
                  <h3
                    className="text-2xl md:text-[28px] font-bold tracking-tight leading-[1.2] group-hover:opacity-80"
                    style={{ color: "var(--brand-ink)" }}
                  >
                    {essay.title}
                  </h3>
                  <p
                    className="mt-3 text-[15px] leading-relaxed line-clamp-2"
                    style={{ color: "var(--brand-muted)" }}
                  >
                    {essay.subtitle}
                  </p>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap"
                  style={{ color: "var(--brand-accent)" }}
                >
                  Read
                  <ArrowRight
                    size={15}
                    strokeWidth={2}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </span>
              </Link>
            ))}
            {matches.length === 0 && (
              <p
                className="py-16 text-center text-lg"
                style={{ color: "var(--brand-muted)" }}
              >
                No essays under this tag yet. Check back soon.
              </p>
            )}
          </div>
        </div>
      </section>

      <section
        className="py-16 px-6 md:px-12 lg:px-20"
        style={{
          background: "var(--brand-cream-soft)",
          borderTop: "1px solid var(--brand-line)",
        }}
      >
        <div className="max-w-6xl mx-auto">
          <p
            className="text-[12px] font-semibold uppercase tracking-[0.28em] mb-5"
            style={{ color: "var(--brand-accent)" }}
          >
            Browse other themes
          </p>
          <div className="flex flex-wrap gap-2.5">
            {otherTags.map((t) => (
              <Link
                key={t.slug}
                to={`/tags/${t.slug}`}
                data-testid={`tag-chip-${t.slug}`}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-300 hover:-translate-y-0.5"
                style={{
                  background: "var(--brand-cream)",
                  border: "1px solid var(--brand-line)",
                  color: "var(--brand-ink-soft)",
                }}
              >
                #{t.label.toLowerCase()}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TagPage;
